import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Eye, Mail, Phone, Building2, Share2, Heart, Tag } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getListing } from '../lib/api';
import { formatPrice, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fix image URLs - convert /uploads/ to /api/uploads/ and ensure full URL
const fixImageUrl = (url) => {
    if (!url) return null;
    // If already full URL with correct path, return as-is
    if (url.includes('/api/uploads/')) return url;
    // If it's a relative /uploads/ path, convert to /api/uploads/
    if (url.includes('/uploads/') && !url.includes('/api/uploads/')) {
        const filename = url.split('/uploads/').pop();
        return `${BACKEND_URL}/api/uploads/${filename}`;
    }
    // If full URL without /api/, fix it
    if (url.startsWith('http') && url.includes('/uploads/') && !url.includes('/api/uploads/')) {
        const filename = url.split('/uploads/').pop();
        return `${BACKEND_URL}/api/uploads/${filename}`;
    }
    return url;
};

const ListingDetailPage = () => {
    const { id } = useParams();
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(0);

    useEffect(() => {
        const fetchListing = async () => {
            try {
                const response = await getListing(id);
                setListing(response.data);
            } catch (error) {
                console.error('Error fetching listing:', error);
                toast.error('Listing not found');
            } finally {
                setLoading(false);
            }
        };
        fetchListing();
    }, [id]);

    const handleShare = () => {
        navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard!');
    };

    if (loading) {
        return (
            <Layout>
                <div className="max-w-7xl mx-auto px-4 py-8">
                    <div className="animate-pulse">
                        <div className="h-8 w-32 bg-gray-200 rounded mb-6" />
                        <div className="grid lg:grid-cols-2 gap-8">
                            <div className="aspect-[4/3] bg-gray-200 rounded-lg" />
                            <div className="space-y-4">
                                <div className="h-8 bg-gray-200 rounded w-3/4" />
                                <div className="h-6 bg-gray-200 rounded w-1/2" />
                                <div className="h-32 bg-gray-200 rounded" />
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!listing) {
        return (
            <Layout>
                <div className="max-w-7xl mx-auto px-4 py-16 text-center">
                    <h1 className="text-2xl font-bold mb-4">Listing Not Found</h1>
                    <p className="text-gray-600 mb-6">The listing you're looking for doesn't exist or has been removed.</p>
                    <Link to="/listings">
                        <Button>Browse All Listings</Button>
                    </Link>
                </div>
            </Layout>
        );
    }

    const placeholderImage = `https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600&fit=crop`;
    const images = listing.images?.length > 0 
        ? listing.images.map(img => fixImageUrl(img)) 
        : [placeholderImage];

    return (
        <Layout>
            <div className="bg-gray-50 min-h-screen py-8" data-testid="listing-detail-page">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Back Link */}
                    <Link to="/listings" className="inline-flex items-center gap-2 text-gray-600 hover:text-navy mb-6" data-testid="back-link">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Listings
                    </Link>

                    <div className="grid lg:grid-cols-2 gap-8">
                        {/* Images */}
                        <div>
                            <div className="aspect-[4/3] bg-white rounded-lg overflow-hidden shadow-sm mb-4">
                                <img
                                    src={images[selectedImage]}
                                    alt={listing.title}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            {images.length > 1 && (
                                <div className="flex gap-2 overflow-x-auto pb-2">
                                    {images.map((img, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setSelectedImage(idx)}
                                            className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${
                                                selectedImage === idx ? 'border-gold' : 'border-transparent'
                                            }`}
                                        >
                                            <img src={img} alt="" className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Details */}
                        <div>
                            <div className="bg-white rounded-lg shadow-sm p-6">
                                {/* Category Badges */}
                                <div className="flex items-center gap-2 mb-4">
                                    <Badge variant="secondary">{listing.category_name}</Badge>
                                    {listing.subcategory_name && (
                                        <Badge variant="outline">{listing.subcategory_name}</Badge>
                                    )}
                                </div>

                                {/* Title & Price */}
                                <h1 className="font-heading text-2xl sm:text-3xl font-bold text-navy mb-2" data-testid="listing-title">
                                    {listing.title}
                                </h1>
                                <div className="text-3xl font-bold text-gold mb-4" data-testid="listing-price">
                                    {formatPrice(listing.price)}
                                </div>

                                {/* Meta Info */}
                                <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-6 pb-6 border-b">
                                    <div className="flex items-center gap-1">
                                        <MapPin className="w-4 h-4" />
                                        {listing.location}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Clock className="w-4 h-4" />
                                        Posted {formatDate(listing.created_at)}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Eye className="w-4 h-4" />
                                        {listing.views} views
                                    </div>
                                </div>

                                {/* Description */}
                                <div className="mb-6">
                                    <h2 className="font-semibold text-lg mb-2">Description</h2>
                                    <p className="text-gray-600 whitespace-pre-line" data-testid="listing-description">
                                        {listing.description}
                                    </p>
                                </div>

                                {/* Business Info */}
                                {listing.business_name && (
                                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                        <div className="flex items-center gap-2 text-navy font-semibold mb-2">
                                            <Building2 className="w-5 h-5" />
                                            {listing.business_name}
                                        </div>
                                    </div>
                                )}

                                {/* Contact Info */}
                                <div className="space-y-3 mb-6">
                                    <h2 className="font-semibold text-lg">Contact Information</h2>
                                    {listing.contact_email && (
                                        <a
                                            href={`mailto:${listing.contact_email}`}
                                            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                            data-testid="contact-email"
                                        >
                                            <Mail className="w-5 h-5 text-gold" />
                                            <span>{listing.contact_email}</span>
                                        </a>
                                    )}
                                    {listing.contact_phone && (
                                        <a
                                            href={`tel:${listing.contact_phone}`}
                                            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                            data-testid="contact-phone"
                                        >
                                            <Phone className="w-5 h-5 text-gold" />
                                            <span>{listing.contact_phone}</span>
                                        </a>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-3">
                                    <Button
                                        variant="outline"
                                        className="flex-1"
                                        onClick={handleShare}
                                        data-testid="share-btn"
                                    >
                                        <Share2 className="w-4 h-4 mr-2" />
                                        Share
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default ListingDetailPage;
